import { ChevronRight, Hexagon, Leaf, Menu, Search, ShoppingCart, Truck, User, X } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useCart } from '../../contexts/CartContext.jsx';
import { assetUrl } from '../../services/storefrontService.js';

export default function Navbar() {
  const { data } = useAppData();
  const { customer } = useAuth();
  const { cart } = useCart();
  const accountHref = customer ? '/dashboard' : '/login';
  const accountLabel = customer ? `Account — ${customer.name}` : 'Sign in';
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const navigation = data?.navigation || [];
  const settings = data?.settings || {};
  const logo = settings.logo;

  function submitSearch(event) {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <>
      <div className="announce-bar">
        <div className="container announce-inner">
          <span className="announce-item"><Hexagon size={13} /> Family owned since 2003</span>
          <span className="announce-item"><Leaf size={13} /> Raw Mediterranean honey &amp; bee products</span>
          <span className="announce-item"><Truck size={13} /> Orders shipped within 24 hours</span>
        </div>
      </div>

      <header className="site-header">
        <div className="container nav-inner">
          <button className="icon-button mobile-only" type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu size={22} />
          </button>

          <Link className="brand" to="/">
            {logo ? <img src={assetUrl(logo)} alt="La Bella Trading" /> : <span>La Bella Trading</span>}
          </Link>

          <nav className="desktop-nav" aria-label="Main navigation">
            {navigation.map((item) => (
              <div className="nav-group" key={item.id}>
                <NavLink to={item.href}>{item.name}</NavLink>
                {item.children?.length ? (
                  <div className="nav-mega">
                    {item.children.map((child) => (
                      <Link className="nav-mega-item" key={child.id} to={child.href}>
                        {child.photo ? <img src={assetUrl(child.photo)} alt="" /> : null}
                        <span className="cap">{child.name.toLowerCase()}</span>
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            <NavLink to="/about">About</NavLink>
            <NavLink to="/services">Services</NavLink>
            <NavLink to="/gallery">Gallery</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </nav>

          <div className="nav-actions">
            <button className="icon-button" type="button" onClick={() => setSearchOpen(true)} aria-label="Search">
              <Search size={20} />
            </button>
            <Link className={customer ? 'icon-button desktop-only account-signed-in' : 'icon-button desktop-only'} to={accountHref} aria-label={accountLabel} title={accountLabel}>
              <User size={20} />
            </Link>
            <Link className="icon-button cart-link" to="/cart" aria-label="Cart">
              <ShoppingCart size={22} />
              <span className="cart-count">{cart.quantity}</span>
            </Link>
          </div>
        </div>

        {menuOpen ? (
          <div className="drawer-backdrop" onClick={() => setMenuOpen(false)}>
            <aside className="mobile-drawer" onClick={(event) => event.stopPropagation()}>
              <div className="drawer-head">
                <strong>La Bella Trading</strong>
                <button className="icon-button" type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                  <X size={20} />
                </button>
              </div>
              <nav>
                <Link to="/" onClick={() => setMenuOpen(false)}>Home <ChevronRight size={17} /></Link>
                {navigation.flatMap((item) => item.children || []).map((item) => (
                  <Link key={item.id} to={item.href} onClick={() => setMenuOpen(false)}>
                    <span className="cap">{item.name.toLowerCase()}</span> <ChevronRight size={17} />
                  </Link>
                ))}
                <Link to="/about" onClick={() => setMenuOpen(false)}>About <ChevronRight size={17} /></Link>
                <Link to="/services" onClick={() => setMenuOpen(false)}>Services <ChevronRight size={17} /></Link>
                <Link to="/gallery" onClick={() => setMenuOpen(false)}>Gallery <ChevronRight size={17} /></Link>
                <Link to={accountHref} onClick={() => setMenuOpen(false)}>{customer ? 'My Account' : 'Sign In'} <ChevronRight size={17} /></Link>
                <Link to="/contact" onClick={() => setMenuOpen(false)}>Contact <ChevronRight size={17} /></Link>
              </nav>
              <div className="drawer-foot">
                {settings.contactEmail ? <span>{settings.contactEmail}</span> : null}
                {settings.contactPhone ? <span>{settings.contactPhone}</span> : null}
              </div>
            </aside>
          </div>
        ) : null}

        {searchOpen ? (
          <div className="search-overlay" onClick={() => setSearchOpen(false)}>
            <form className="search-panel" onSubmit={submitSearch} onClick={(event) => event.stopPropagation()}>
              <div className="search-panel-head">
                <label htmlFor="site-search">Search the pantry</label>
                <button className="icon-button" type="button" onClick={() => setSearchOpen(false)} aria-label="Close search">
                  <X size={20} />
                </button>
              </div>
              <div className="search-row">
                <div className="search-input-wrap">
                  <Search size={19} />
                  <input
                    id="site-search"
                    autoFocus
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Honey, propolis, olive oil, soap..."
                  />
                </div>
                <button className="button button-honey" type="submit">Search</button>
              </div>
              <p className="search-hint">Try &ldquo;milk thistle&rdquo;, &ldquo;royal jelly&rdquo;, or &ldquo;olive oil&rdquo;.</p>
            </form>
          </div>
        ) : null}
      </header>
    </>
  );
}
